import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import {
    LuChevronDown, LuChevronLeft, LuChevronRight, LuChevronsUpDown, LuChevronUp,
    LuCircleCheck, LuCircleX, LuClock, LuEye, LuLayoutGrid, LuList, LuPencil,
    LuPlus, LuRefreshCw, LuSearch, LuTrash2, LuTriangleAlert,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import Select from '../components/Select';
import TemplateBubble, { lerComponentes } from '../components/TemplateBubble';
import {
    CardConfirm, FiltroPill, ListCard, ListCards, ListRow, PerPage, RowAction, RowActions, RowConfirm,
} from '../components/ListKit';
import WhatsAppConnectionGuard from '../components/WhatsAppConnectionGuard';
import { useCelular } from '../hooks/useCelular';
import { PERMISSOES, temPermissao } from '../config/permissions';
import { MetaTemplateService, type MetaTemplate } from '../services/MetaTemplateService';
import {
    EDITAVEL, pesoQualidade, rotuloIdioma, rotuloQualidade, STATUS_LABEL, STATUS_TOM, tomQualidade,
} from '../utils/metaTemplateLabels';
import '../components/ListKit.css';
// CampaignTemplatesPage.css é um dos QUATRO arquivos que declaram
// .management-table, .filters-container, .filter-input e .actions-cell —
// classes consumidas por 15 telas ainda não migradas. Esta tela não usa mais
// nenhuma delas, mas o import fica: sem ele o arquivo sai do bundle e as
// outras quebram. Ver docs/designsystem/estado-do-redesign.md.
import './CampaignTemplatesPage.css';
import './TemplatesScreen.css';

/* ─── Domínio ──────────────────────────────────────────────────── */

type Status = MetaTemplate['status'];

const STATUS_ICONE: Record<Status, typeof LuClock> = {
    APPROVED: LuCircleCheck,
    PENDING: LuClock,
    IN_REVIEW: LuClock,
    REJECTED: LuCircleX,
    FLAGGED: LuTriangleAlert,
    DISABLED: LuCircleX,
    PAUSED: LuClock,
};

const COLUNAS = '1fr 150px 112px 92px 118px 78px';
const POR_PAGINA = [25, 50, 100] as const;

/* A categoria não tem coluna na tabela — quem a mostra é o cartão do celular,
   onde cabem duas informações de identidade na mesma linha de apoio. */
const CATEGORIA_LABEL: Record<MetaTemplate['categoria'], string> = {
    MARKETING: 'Marketing', UTILITY: 'Utilidade', AUTHENTICATION: 'Autenticação',
};

type Visao = 'lista' | 'bloco';
type ColunaOrdem = 'nome' | 'status' | 'qualidade' | 'idioma' | 'criado';
type Direcao = 'asc' | 'desc';

/** Primeiro clique: texto sobe, número e data descem. */
const DIRECAO_PADRAO: Record<ColunaOrdem, Direcao> = {
    nome: 'asc', status: 'asc', idioma: 'asc', qualidade: 'desc', criado: 'desc',
};

const ORDENS_BLOCO = [
    { value: 'nome:asc', label: 'Nome (A–Z)' },
    { value: 'nome:desc', label: 'Nome (Z–A)' },
    { value: 'criado:desc', label: 'Mais recentes' },
    { value: 'criado:asc', label: 'Mais antigos' },
    { value: 'status:asc', label: 'Status' },
    { value: 'qualidade:desc', label: 'Qualidade' },
];

/* ─── Estado que sobrevive à navegação ─────────────────────────────
   Recorte de trabalho (busca, filtro, ordem, página) fica na sessão: ao
   voltar da página do template a lista tem que estar como ele deixou.
   A VISÃO é preferência, não recorte, então fica no localStorage — igual
   ao mural de Anotações. */

const CHAVE_VISAO = 'lemeai_templates_visao';
const CHAVE_RECORTE = 'lemeai_templates_recorte';

interface Recorte {
    busca: string;
    status: string | null;
    qualidade: string | null;
    ordem: string;
    porPagina: number;
    pagina: number;
}

const RECORTE_VAZIO: Recorte = {
    busca: '', status: null, qualidade: null, ordem: 'nome:asc', porPagina: 25, pagina: 1,
};

const lerRecorte = (): Recorte => {
    try {
        const cru = sessionStorage.getItem(CHAVE_RECORTE);
        return cru ? { ...RECORTE_VAZIO, ...JSON.parse(cru) } : RECORTE_VAZIO;
    } catch {
        return RECORTE_VAZIO;
    }
};

const dataCurta = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

/* ─── Tela ─────────────────────────────────────────────────────── */

const CampaignTemplatesPage = () => {
    const navegar = useNavigate();
    const celular = useCelular();
    /* A tela abre com `marketing_templates`; criar, editar, excluir e
       sincronizar com a Meta pedem `marketing_templates.gerenciar`. */
    const podeGerenciar = temPermissao(PERMISSOES.marketingTemplatesGerenciar);

    const [templates, setTemplates] = useState<MetaTemplate[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [sincronizando, setSincronizando] = useState(false);

    const [visao, setVisao] = useState<Visao>(
        () => (localStorage.getItem(CHAVE_VISAO) as Visao) || 'lista');

    const inicial = useMemo(lerRecorte, []);
    const [busca, setBusca] = useState(inicial.busca);
    const [status, setStatus] = useState<string | null>(inicial.status);
    const [qualidade, setQualidade] = useState<string | null>(inicial.qualidade);
    const [ordem, setOrdem] = useState(inicial.ordem);
    const [porPagina, setPorPagina] = useState(inicial.porPagina);
    const [pagina, setPagina] = useState(inicial.pagina);

    const [aConfirmar, setAConfirmar] = useState<number | null>(null);
    const [apagando, setApagando] = useState(false);

    useEffect(() => { localStorage.setItem(CHAVE_VISAO, visao); }, [visao]);

    useEffect(() => {
        const recorte: Recorte = { busca, status, qualidade, ordem, porPagina, pagina };
        sessionStorage.setItem(CHAVE_RECORTE, JSON.stringify(recorte));
    }, [busca, status, qualidade, ordem, porPagina, pagina]);

    const carregar = useCallback(async () => {
        try {
            const res = await MetaTemplateService.getAll();
            if (res.sucesso) setTemplates(res.dados || []);
            else aviso.erro(res.mensagem || 'Não foi possível carregar os templates.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setCarregando(false);
        }
    }, []);

    /* Abre com o que está salvo e, em seguida, sincroniza em silêncio: o
       status de verdade mora na Meta, não aqui. */
    useEffect(() => {
        let vivo = true;
        const iniciar = async () => {
            await carregar();
            // Sem a chave a API recusaria (403) e o aviso global apareceria à toa.
            if (!vivo || !podeGerenciar) return;
            setSincronizando(true);
            try {
                const res = await MetaTemplateService.sincronizar();
                if (vivo && res.sucesso) await carregar();
            } catch {
                // Silencioso de propósito: a lista local já está na tela.
            } finally {
                if (vivo) setSincronizando(false);
            }
        };
        iniciar();
        return () => { vivo = false; };
    }, [carregar, podeGerenciar]);

    const sincronizar = async () => {
        setSincronizando(true);
        try {
            const res = await MetaTemplateService.sincronizar();
            if (res.sucesso) {
                await carregar();
                const d = res.dados;
                aviso.ok(d
                    ? `Sincronizado: ${d.criados} novo(s), ${d.atualizados} atualizado(s).`
                    : 'Sincronizado com a Meta.');
            } else {
                aviso.erro(res.mensagem || 'A Meta não respondeu à sincronização.');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSincronizando(false);
        }
    };

    const excluir = async (t: MetaTemplate) => {
        setApagando(true);
        try {
            const res = await MetaTemplateService.remove(t.metaTemplateId);
            if (res.sucesso) {
                setTemplates(prev => prev.filter(x => x.metaTemplateId !== t.metaTemplateId));
                setAConfirmar(null);
                aviso.ok(`Template "${t.nome}" excluído na Meta.`);
            } else {
                aviso.erro(res.mensagem || 'A Meta não deixou excluir este template.');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setApagando(false);
        }
    };

    /* ─── Filtro, ordem e página ──────────────────────────────── */

    const opcoesStatus = useMemo(() => {
        const conta = new Map<string, number>();
        templates.forEach(t => {
            const r = STATUS_LABEL[t.status];
            conta.set(r, (conta.get(r) ?? 0) + 1);
        });
        return [...conta.entries()].map(([valor, total]) => ({ valor, total }))
            .sort((a, b) => a.valor.localeCompare(b.valor, 'pt-BR'));
    }, [templates]);

    const opcoesQualidade = useMemo(() => {
        const conta = new Map<string, number>();
        templates.forEach(t => {
            const r = rotuloQualidade(t.qualidade);
            conta.set(r, (conta.get(r) ?? 0) + 1);
        });
        return [...conta.entries()].map(([valor, total]) => ({ valor, total }));
    }, [templates]);

    const [colunaOrdem, direcao] = useMemo(() => {
        const [c, d] = ordem.split(':');
        return [c as ColunaOrdem, (d as Direcao) ?? 'asc'] as const;
    }, [ordem]);

    const filtrados = useMemo(() => {
        const alvo = busca.trim().toLowerCase();
        return templates.filter(t => {
            if (status && STATUS_LABEL[t.status] !== status) return false;
            if (qualidade && rotuloQualidade(t.qualidade) !== qualidade) return false;
            if (!alvo) return true;
            if (t.nome.toLowerCase().includes(alvo)) return true;
            /* Na visão bloco o texto está na tela, então a busca alcança ele.
               Na lista não: resultado sem correspondência visível parece erro. */
            if (visao === 'bloco') {
                return lerComponentes(t.componentesJson).textoBody.toLowerCase().includes(alvo);
            }
            return false;
        });
    }, [templates, busca, status, qualidade, visao]);

    const ordenados = useMemo(() => {
        const sinal = direcao === 'asc' ? 1 : -1;
        const chave = (t: MetaTemplate): string | number => {
            switch (colunaOrdem) {
                case 'status': return STATUS_LABEL[t.status];
                case 'idioma': return t.idioma ?? '';
                case 'qualidade': return pesoQualidade(t.qualidade);
                case 'criado': return new Date(t.criadoEm).getTime() || 0;
                default: return t.nome;
            }
        };
        return [...filtrados].sort((a, b) => {
            const x = chave(a);
            const y = chave(b);
            /* Vazio vai para o fim nos dois sentidos — é o que a regra do
               cabeçalho ordenável pede. */
            const xVazio = x === '' || x === 0;
            const yVazio = y === '' || y === 0;
            if (xVazio !== yVazio) return xVazio ? 1 : -1;
            if (typeof x === 'number' && typeof y === 'number') return (x - y) * sinal;
            return String(x).localeCompare(String(y), 'pt-BR') * sinal;
        });
    }, [filtrados, colunaOrdem, direcao]);

    const totalPaginas = Math.max(1, Math.ceil(ordenados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = ordenados.slice(inicio, inicio + porPagina);

    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, status, qualidade, porPagina]);

    const aprovados = templates.filter(t => t.status === 'APPROVED').length;
    const semFiltro = !busca.trim() && !status && !qualidade;
    const vazioDeVerdade = !carregando && templates.length === 0;
    const semResultado = !carregando && templates.length > 0 && ordenados.length === 0;

    const ordenarPor = (col: ColunaOrdem) => {
        setOrdem(colunaOrdem === col
            ? `${col}:${direcao === 'asc' ? 'desc' : 'asc'}`
            : `${col}:${DIRECAO_PADRAO[col]}`);
    };

    const limparFiltros = () => { setBusca(''); setStatus(null); setQualidade(null); };

    /* Função, não componente: um componente declarado aqui nasceria novo a
       cada render e o botão perderia o foco logo depois do clique. */
    const cabecalho = (col: ColunaOrdem, rotulo: string) => {
        const ativo = colunaOrdem === col;
        const Seta = !ativo ? LuChevronsUpDown : direcao === 'asc' ? LuChevronUp : LuChevronDown;
        const proxima = ativo
            ? (direcao === 'asc' ? 'decrescente' : 'crescente')
            : DIRECAO_PADRAO[col] === 'asc' ? 'crescente' : 'decrescente';
        return (
            <button type="button" className={`lk-head lk-head-sort ${ativo ? 'is-on' : ''}`}
                onClick={() => ordenarPor(col)}
                aria-sort={ativo ? (direcao === 'asc' ? 'ascending' : 'descending') : 'none'}
                title={`Ordenar por ${rotulo.toLowerCase()} (${proxima})`}>
                <span>{rotulo}</span><Seta size={12} aria-hidden="true" />
            </button>
        );
    };

    const Etiqueta = ({ s }: { s: Status }) => {
        const Icone = STATUS_ICONE[s];
        return (
            <span className={`tpl-st ${STATUS_TOM[s]}`}>
                <Icone size={11} aria-hidden="true" />{STATUS_LABEL[s]}
            </span>
        );
    };

    /* Sem nota não ganha ponto de cor: a Meta só pontua depois de entregar
       volume, e ponto cinza parece estado ruim. */
    const Qualidade = ({ q }: { q: MetaTemplate['qualidade'] }) => {
        const tom = tomQualidade(q);
        if (tom === null) return <span className="tpl-dash">Sem nota</span>;
        return <span className={`tpl-q ${tom}`}><i />{rotuloQualidade(q)}</span>;
    };

    /* A porta da linha é o LÁPIS e ela abre a página JÁ EM EDIÇÃO — pedido
       dele em 18/09: *"faz com que o botão de visualizar saia e vá direto
       para Editar, ou seja, trocar o olho pelo lápis"*. Casa com a exceção
       já escrita no `padrao-de-tela.md` §2: registro que abre em página
       fica com lápis + lixeira.

       O olho sobrevive numa situação só: nos cinco status que a Meta não
       deixa alterar, onde um lápis prometeria uma edição impossível. É o
       mesmo teste da regra — *o botão só existe onde existe a ação*. */
    const editavel = (t: MetaTemplate) => podeGerenciar && EDITAVEL.includes(t.status);

    const abrir = (t: MetaTemplate) => navegar(
        `/campaign-templates/${t.metaTemplateId}${editavel(t) ? '?editar=1' : ''}`);

    /** A pergunta carrega o alcance: na Meta, todos os idiomas, sem desfazer. */
    const perguntaExclusao = (nome: string) =>
        `Excluir "${nome}" na Meta? Apaga o template em todos os idiomas e não tem desfazer.`;

    const acoes = (
        <div className="lk-actions">
            <div className="tpl-vis" role="group" aria-label="Formato da lista">
                <button type="button" className={visao === 'lista' ? 'is-on' : ''}
                    onClick={() => setVisao('lista')} title="Lista"
                    aria-pressed={visao === 'lista'}>
                    <LuList size={14} />
                </button>
                <button type="button" className={visao === 'bloco' ? 'is-on' : ''}
                    onClick={() => setVisao('bloco')} title="Bloco"
                    aria-pressed={visao === 'bloco'}>
                    <LuLayoutGrid size={14} />
                </button>
            </div>
            {/* Sem template nenhum, as duas portas moram no aviso — no topo
                seriam a mesma coisa duas vezes. */}
            {!vazioDeVerdade && podeGerenciar && (
                <>
                    <button type="button" className="lk-btn" onClick={sincronizar} disabled={sincronizando}>
                        <LuRefreshCw size={14} className={sincronizando ? "tpl-rodando" : undefined} />
                        {sincronizando ? 'Sincronizando…' : 'Sincronizar'}
                    </button>
                    <button type="button" className="lk-btn is-primary"
                        onClick={() => navegar('/campaign-templates/novo')}>
                        <LuPlus size={15} /> Novo template
                    </button>
                </>
            )}
        </div>
    );

    const filtros = (
        <div className="lk-filters">
            <label className="lk-search">
                <LuSearch size={14} aria-hidden="true" />
                <input
                    type="text"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder={visao === 'bloco' ? 'Buscar por nome ou texto' : 'Buscar por nome'}
                    aria-label="Buscar template"
                />
            </label>

            <FiltroPill rotulo="Status" valor={status} opcoes={opcoesStatus} onChange={setStatus} />
            <FiltroPill rotulo="Qualidade" valor={qualidade} opcoes={opcoesQualidade}
                onChange={setQualidade} />

            {/* Cartão não tem cabeçalho para ordenar, então na visão bloco a
                ordem vira pílula — e escreve o MESMO estado da lista, para
                trocar de visão não perder a ordem. */}
            {visao === 'bloco' && (
                <Select
                    size="pill"
                    block={false}
                    value={ordem}
                    options={ORDENS_BLOCO}
                    onChange={setOrdem}
                    ariaLabel="Ordem dos blocos"
                />
            )}

            <span className="lk-count">
                {carregando ? 'Carregando…' : `${ordenados.length} ${ordenados.length === 1 ? 'template' : 'templates'}`}
                {!carregando && semFiltro && aprovados > 0 && ` · ${aprovados} aprovado${aprovados === 1 ? '' : 's'}`}
            </span>
        </div>
    );

    const vazio = (
        <div className="tpl-blank">
            <strong>Nenhum template ainda</strong>
            <p>
                Template é a mensagem que a Meta precisa aprovar antes de você abrir conversa.
                Se já existem templates nessa conta do WhatsApp, o <b>Sincronizar</b> traz todos.
            </p>
            {podeGerenciar && (
                <div className="tpl-tools" style={{ justifyContent: 'center' }}>
                    <button type="button" className="tpl-btn" onClick={sincronizar} disabled={sincronizando}>
                        <LuRefreshCw size={14} /> Sincronizar com a Meta
                    </button>
                    <button type="button" className="tpl-btn is-primary"
                        onClick={() => navegar('/campaign-templates/novo')}>
                        <LuPlus size={15} /> Criar o primeiro
                    </button>
                </div>
            )}
        </div>
    );

    const nenhumResultado = (
        <div className="tpl-blank">
            <strong>Nada com esse recorte</strong>
            <p>Os {templates.length} templates da conta continuam aqui — o filtro é que não deixa nenhum passar.</p>
            <div className="tpl-tools" style={{ justifyContent: 'center' }}>
                <button type="button" className="tpl-btn" onClick={limparFiltros}>Limpar filtros</button>
            </div>
        </div>
    );

    /* ─── Visão lista ─────────────────────────────────────────── */

    /* Os dois avisos de "nada aqui" servem à tabela (no computador) e aos
       cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <>
            {/* Na lista o vazio é o aviso do kit, irmão do trilho: é o que deixa
                a moldura centrá-lo na altura do cartão. Dentro do corpo ele
                colava no cabeçalho, no topo de um cartão vazio. */}
            {vazioDeVerdade && (
                <div className="lk-blank">
                    <div className="lk-blank-title">Nenhum template ainda</div>
                    <p className="lk-blank-text">
                        Template é a mensagem que a Meta precisa aprovar antes de você abrir conversa.
                        Se já existem templates nessa conta do WhatsApp, o <b>Sincronizar</b> traz todos.
                    </p>
                    {podeGerenciar && (
                        <div className="lk-actions" style={{ justifyContent: 'center' }}>
                            <button type="button" className="lk-btn" onClick={sincronizar} disabled={sincronizando}>
                                <LuRefreshCw size={14} className={sincronizando ? "tpl-rodando" : undefined} />
                                {sincronizando ? 'Sincronizando…' : 'Sincronizar com a Meta'}
                            </button>
                            <button type="button" className="lk-btn is-primary"
                                onClick={() => navegar('/campaign-templates/novo')}>
                                <LuPlus size={15} /> Criar o primeiro
                            </button>
                        </div>
                    )}
                </div>
            )}
            {semResultado && (
                <div className="lk-blank">
                    <div className="lk-blank-title">Nada com esse recorte</div>
                    <p className="lk-blank-text">
                        Os {templates.length} templates da conta continuam aqui — o filtro é que não deixa nenhum passar.
                    </p>
                    <button type="button" className="lk-btn" onClick={limparFiltros}>Limpar filtros</button>
                </div>
            )}
        </>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, ordenados.length)} de {ordenados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual <= 1} onClick={() => setPagina(p => p - 1)}>
                    <LuChevronLeft size={14} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual >= totalPaginas} onClick={() => setPagina(p => p + 1)}>
                    <LuChevronRight size={14} />
                </button>
            </span>
        </div>
    );

    const Lista = (
        <>
            {/* Celular: a grade de seis colunas não cabe em 358px, então a linha
                vira cartão — nome, idioma e categoria, com o status da Meta como
                estado. Qualidade e data ficam um toque adiante, no template. */}
            {celular ? (
                <>
                    <ListCards>
                        {carregando
                            ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                            : visiveis.map(t => (
                                aConfirmar === t.metaTemplateId ? (
                                    <CardConfirm key={t.metaTemplateId}
                                        question={perguntaExclusao(t.nome)}
                                        busy={apagando}
                                        onCancel={() => setAConfirmar(null)}
                                        onConfirm={() => excluir(t)} />
                                ) : (
                                    <ListCard
                                        key={t.metaTemplateId}
                                        titulo={<span className="tpl-card-nm">{t.nome}</span>}
                                        apoio={`${rotuloIdioma(t.idioma)} · ${CATEGORIA_LABEL[t.categoria]}`}
                                        estado={<Etiqueta s={t.status} />}
                                        label={editavel(t) ? `Editar ${t.nome}` : `Ver ${t.nome}`}
                                        onOpen={() => abrir(t)}
                                        acoes={podeGerenciar ? (
                                            <RowAction icon={<LuTrash2 size={15} />} danger
                                                label={`Excluir ${t.nome}`}
                                                onClick={() => setAConfirmar(t.metaTemplateId)} />
                                        ) : undefined}
                                    />
                                )
                            ))}
                    </ListCards>
                    {listaVazia}
                </>
            ) : (
            <div className="lk-table">
                <div className="lk-scroll">
                    <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                        {cabecalho('nome', 'Template')}
                        {cabecalho('status', 'Status')}
                        {cabecalho('qualidade', 'Qualidade')}
                        {cabecalho('idioma', 'Idioma')}
                        {cabecalho('criado', 'Criado')}
                        <div className="lk-head lk-head-acts">Ações</div>
                    </div>

                    <div className="lk-body">
                        {carregando
                            ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                            : visiveis.map(t => (
                                aConfirmar === t.metaTemplateId ? (
                                    <RowConfirm key={t.metaTemplateId}
                                        question={perguntaExclusao(t.nome)}
                                        busy={apagando}
                                        onCancel={() => setAConfirmar(null)}
                                        onConfirm={() => excluir(t)} />
                                ) : (
                                    <ListRow key={t.metaTemplateId} columns={COLUNAS}>
                                        <span className="lk-cell lk-strong">
                                            <span className="tpl-card-nm">{t.nome}</span>
                                        </span>
                                        <span className="lk-cell"><Etiqueta s={t.status} /></span>
                                        <span className="lk-cell lk-muted"><Qualidade q={t.qualidade} /></span>
                                        <span className="lk-cell">
                                            <span className="tpl-lang">{t.idioma}</span>
                                        </span>
                                        <span className="lk-cell lk-muted">
                                            <span>{dataCurta(t.criadoEm)}</span>
                                        </span>
                                        <RowActions>
                                            <RowAction
                                                icon={editavel(t) ? <LuPencil size={14} /> : <LuEye size={14} />}
                                                label={editavel(t) ? `Editar ${t.nome}` : `Ver ${t.nome}`}
                                                onClick={() => abrir(t)} />
                                            {podeGerenciar && (
                                                <RowAction icon={<LuTrash2 size={14} />} danger
                                                    label={`Excluir ${t.nome}`}
                                                    onClick={() => setAConfirmar(t.metaTemplateId)} />
                                            )}
                                        </RowActions>
                                    </ListRow>
                                )
                            ))}

                    </div>
                </div>

                {listaVazia}
            </div>
            )}

            {!carregando && ordenados.length > 0 && rodape}
        </>
    );

    /* ─── Visão bloco ─────────────────────────────────────────── */

    const Bloco = (
        <div className="tpl-mural">
            {carregando
                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="tpl-skel" />)
                : visiveis.map(t => {
                    const visual = lerComponentes(t.componentesJson, t.caminhoMidiaHeader);
                    const confirmando = aConfirmar === t.metaTemplateId;
                    return (
                        <article key={t.metaTemplateId} className="tpl-card">
                            <header className="tpl-card-h">
                                <span className="tpl-card-nm" title={t.nome}>{t.nome}</span>
                                <Etiqueta s={t.status} />
                            </header>

                            <div className="tpl-card-b">
                                <TemplateBubble visual={visual} compact maxBotoes={2}
                                    vazio="Este template não tem corpo salvo." />
                            </div>

                            {confirmando ? (
                                <div className="tpl-card-confirm" role="alert">
                                    <p>Excluir na Meta? Todos os idiomas, sem desfazer.</p>
                                    <button type="button" className="tpl-cb" disabled={apagando}
                                        onClick={() => setAConfirmar(null)}>Cancelar</button>
                                    <button type="button" className="tpl-cb is-danger" disabled={apagando}
                                        onClick={() => excluir(t)}>
                                        {apagando ? 'Aguarde…' : 'Excluir'}
                                    </button>
                                </div>
                            ) : (
                                <footer className="tpl-card-f">
                                    <Qualidade q={t.qualidade} />
                                    <span className="tpl-lang">{t.idioma}</span>
                                    <span className="tpl-acts">
                                        <button type="button" className="tpl-act"
                                            aria-label={editavel(t) ? `Editar ${t.nome}` : `Ver ${t.nome}`}
                                            title={editavel(t) ? `Editar ${t.nome}` : `Ver ${t.nome}`}
                                            onClick={() => abrir(t)}>
                                            {editavel(t) ? <LuPencil size={14} /> : <LuEye size={14} />}
                                        </button>
                                        {podeGerenciar && (
                                            <button type="button" className="tpl-act is-danger"
                                                aria-label={`Excluir ${t.nome}`} title={`Excluir ${t.nome}`}
                                                onClick={() => setAConfirmar(t.metaTemplateId)}>
                                                <LuTrash2 size={14} />
                                            </button>
                                        )}
                                    </span>
                                </footer>
                            )}
                        </article>
                    );
                })}

            {vazioDeVerdade && vazio}
            {semResultado && nenhumResultado}
        </div>
    );

    const legenda = sincronizando
        ? 'Sincronizando com a Meta…'
        : 'Mensagens aprovadas pela Meta para abrir conversa. Só as aprovadas aparecem no Disparador.';

    return (
        <WhatsAppConnectionGuard>
            {/* `lk-screen` fica nas DUAS visões: além de travar a altura, é
                onde os tokens do ListKit são declarados — sem ela os pedaços
                do kit (busca, pílula, botão primário) ficam sem cor. Medido
                em 18/09: o "Novo template" virava branco no branco. */}
            <div className="page-shell tpl-screen lk-screen">
                <PageHeader title="Templates" subtitle={legenda} actions={acoes} />
                <div className={`page-body ${visao === 'lista' ? 'lk-fill' : 'tpl-mural-body'}`}>
                    {filtros}
                    {visao === 'lista' ? Lista : Bloco}
                </div>
            </div>
        </WhatsAppConnectionGuard>
    );
};

export default CampaignTemplatesPage;
